import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { cx } from './Primitives';
import { IconAlert, IconCheck, IconClose, IconInfo } from './icons';

/* ============================================================================
   Feedback (DESIGN-SYSTEM §14): what the product says back.

   - `Notice` — a banner in the page's flow: a stale source, a refusal, a
     change that is waiting. It stays until what it says stops being true.
   - Toasts — a short confirmation that something just happened ("Saved"),
     which goes by itself. Never the only place an error is said: an error
     stays on the screen, beside what it is about.
   ============================================================================ */

export type NoticeTone = 'info' | 'pos' | 'warn' | 'dang' | 'neutral';

const NOTICE: Record<NoticeTone, { box: string; ink: string; icon: React.ReactNode }> = {
    info: { box: 'bg-info-bg border-info/25', ink: 'text-info', icon: <IconInfo size={18} /> },
    pos: { box: 'bg-pos-bg border-pos/25', ink: 'text-pos', icon: <IconCheck size={18} /> },
    warn: { box: 'bg-warn-bg border-warn/25', ink: 'text-warn', icon: <IconAlert size={18} /> },
    dang: { box: 'bg-dang-bg border-dang/25', ink: 'text-dang', icon: <IconAlert size={18} /> },
    neutral: { box: 'bg-card-2 border-separator', ink: 'text-label-2', icon: <IconInfo size={18} /> },
};

/**
 * A banner: a title that says the fact, a line that says what it means, and
 * the one thing to do about it. `role` is the caller's: "alert" only for
 * something that just went wrong because of what the person did; a standing
 * state ("not checked in for 26 hours") is "status", or nothing.
 */
export function Notice({
    tone = 'info', title, children, action, role, className, compact = false,
}: {
    tone?: NoticeTone;
    title?: React.ReactNode;
    children?: React.ReactNode;
    action?: React.ReactNode;
    role?: 'alert' | 'status';
    className?: string;
    /** Inside a panel or a form: tighter, no icon. */
    compact?: boolean;
}) {
    const t = NOTICE[tone];
    return (
        <div role={role} className={cx('rounded-card border flex gap-3', compact ? 'px-3.5 py-3' : 'px-4 py-3.5', t.box, className)}>
            {!compact && <span className={cx('flex-none mt-px', t.ink)} aria-hidden="true">{t.icon}</span>}
            <div className="min-w-0 flex-1">
                {title && <p className={cx('text-body font-semibold leading-snug', t.ink)}>{title}</p>}
                {children && <div className={cx('text-body text-label-2 leading-relaxed', Boolean(title) && 'mt-0.5')}>{children}</div>}
                {action && <div className="mt-2.5 flex flex-wrap items-center gap-2">{action}</div>}
            </div>
        </div>
    );
}

/* --------------------------------- toasts --------------------------------- */

interface Toast { id: number; tone: 'pos' | 'info' | 'neutral'; text: string }
const ToastContext = createContext<((text: string, tone?: Toast['tone']) => void) | null>(null);

/**
 * Where toasts appear: the bottom of the screen, above a phone's bottom bar,
 * in one polite live region, so a screen reader hears each once. Each stays
 * five seconds, longer while the pointer or focus is on it, and has a close
 * button. Mount once, near the root.
 */
export function ToastRegion({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const next = useRef(1);
    const show = useCallback((text: string, tone: Toast['tone'] = 'pos') => {
        const id = next.current++;
        setToasts(list => [...list.slice(-2), { id, tone, text }]);
    }, []);
    const dismiss = useCallback((id: number) => setToasts(list => list.filter(t => t.id !== id)), []);
    return (
        <ToastContext.Provider value={show}>
            {children}
            <div aria-live="polite" role="status"
                className="fixed z-[70] inset-x-0 bottom-0 pointer-events-none flex flex-col items-center gap-2 px-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-6">
                {toasts.map(t => <ToastItem key={t.id} toast={t} onDone={() => dismiss(t.id)} />)}
            </div>
        </ToastContext.Provider>
    );
}

function ToastItem({ toast, onDone }: { toast: Toast; onDone: () => void }) {
    const [held, setHeld] = useState(false);
    useEffect(() => {
        if (held) return;
        const timer = window.setTimeout(onDone, 5000);
        return () => window.clearTimeout(timer);
    }, [held, onDone]);
    return (
        <div
            onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
            onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}
            className="pointer-events-auto animate-rise flex items-center gap-3 min-h-11 max-w-md w-full sm:w-auto pl-4 pr-1.5 py-1.5 rounded-card bg-chrome text-chrome-label shadow-e3"
        >
            {toast.tone === 'pos' && <span className="text-mark-on-chrome flex-none" aria-hidden="true"><IconCheck size={16} /></span>}
            <span className="text-body font-medium min-w-0 flex-1">{toast.text}</span>
            <button type="button" onClick={onDone} aria-label="Dismiss"
                className="flex-none size-8 grid place-items-center rounded-control text-chrome-label-3 hover:text-chrome-label hover:bg-chrome-2">
                <IconClose size={16} />
            </button>
        </div>
    );
}

/** Says something just happened. Outside a `ToastRegion` it does nothing, so a screen never depends on it. */
export function useToast(): (text: string, tone?: Toast['tone']) => void {
    const show = useContext(ToastContext);
    return useMemo(() => show ?? (() => {}), [show]);
}
